/**
 * Assembly drawing display for a part suggestion.
 *
 * A thumbnail sits directly above the "Was this the right part?" confirm row so
 * the technician can compare the drawing against the machine in front of them
 * before answering. Clicking it opens a pan/zoom lightbox on the 4000px render:
 * an A3 assembly sheet is unreadable at chat-bubble width.
 *
 * The drawings label parts with their item code, not balloon numbers, and
 * highlighting the exact label is a later phase — so the caption names the code
 * to look for, which is what makes the picture actionable today.
 *
 * Several machines have more than one catalogue (base, Rev.1, Rev.2, and one
 * Preliminary draft) and all of them are ingested, because a superseded book can
 * be the only place a dropped part is documented. The lookup ranks
 * newest-revision-first, so this component's job is to say which book a drawing
 * actually came from whenever that is not the plain, released original.
 */

import {
  FileWarning,
  type LucideIcon,
  Maximize2,
  Minus,
  Plus,
  RotateCcw,
  TriangleAlert,
} from "lucide-react";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useRef,
  useState,
} from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import type { PartFigure } from "./types";
import { presentText } from "./utils";

const MIN_SCALE = 1;
const MAX_SCALE = 6;

/** Multiplier applied by the zoom in/out buttons and the +/- keys. */
const SCALE_STEP = 1.5;

/** Scale a double-click jumps to, chosen to make item-code labels legible. */
const DOUBLE_CLICK_SCALE = 3;

/** Wheel delta, in pixels, that corresponds to one e-fold of zoom. Larger is gentler. */
const WHEEL_ZOOM_DIVISOR = 300;

/**
 * Pixel equivalents of the non-pixel wheel delta modes.
 *
 * Firefox reports `DOM_DELTA_LINE` (deltaY ≈ ±3 per notch) for many mice where
 * Chrome reports pixels (≈ ±100), so an unnormalised delta zooms roughly 30×
 * more weakly there. `DOM_DELTA_PAGE` is rare; the scale clamp keeps the
 * resulting jump sane, so a rough screenful is close enough.
 */
const WHEEL_LINE_HEIGHT = 16;
const WHEEL_PAGE_HEIGHT = 400;

/** Pixels the arrow keys pan per press. */
const KEY_PAN_STEP = 60;

interface Point {
  x: number;
  y: number;
}

/** Scale plus translation of the drawing inside the viewer. */
interface View {
  scale: number;
  offset: Point;
}

const ORIGIN: Point = { x: 0, y: 0 };
const IDENTITY_VIEW: View = { scale: MIN_SCALE, offset: ORIGIN };

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Re-scale `current` to `nextScale` while keeping `focal` — a point relative to
 * the viewer's centre, in screen pixels — over the same spot on the drawing.
 *
 * The transform is `translate(offset) scale(scale)` about the element centre,
 * so image point `u` renders at `offset + scale * u`. Inverting that for a fixed
 * `u` gives `offset' = focal - (scale'/scale) * (focal - offset)`.
 */
function zoomView(
  current: View,
  nextScale: number,
  focal: Point,
  bounds: (scale: number) => Point,
): View {
  const scale = clamp(nextScale, MIN_SCALE, MAX_SCALE);
  const ratio = scale / current.scale;
  const limit = bounds(scale);
  return {
    scale,
    offset: {
      x: clamp(focal.x - ratio * (focal.x - current.offset.x), -limit.x, limit.x),
      y: clamp(focal.y - ratio * (focal.y - current.offset.y), -limit.y, limit.y),
    },
  };
}

/**
 * A wheel event's vertical delta in pixels, whatever unit the browser reported
 * it in. `deltaMode` is per-event, not per-browser: the same page sees pixels
 * from a trackpad and lines from a wheel mouse on Firefox.
 */
function wheelDeltaPixels(event: WheelEvent): number {
  switch (event.deltaMode) {
    case 1: // WheelEvent.DOM_DELTA_LINE
      return event.deltaY * WHEEL_LINE_HEIGHT;
    case 2: // WheelEvent.DOM_DELTA_PAGE
      return event.deltaY * WHEEL_PAGE_HEIGHT;
    default: // WheelEvent.DOM_DELTA_PIXEL
      return event.deltaY;
  }
}

/** The drawing's own name, falling back to the assembly's item code. */
function figureTitle(figure: PartFigure): string {
  return presentText(figure.assembly_name) ?? `Assembly ${figure.assembly_item_id}`;
}

/**
 * Where the drawing sits in the catalogue: chapter and page, whichever are
 * known. Both are nullable — not every block header carries a chapter.
 */
function figureSource(figure: PartFigure): string | null {
  const parts: string[] = [];
  const chapter = presentText(figure.chapter);
  if (chapter) {
    parts.push(chapter);
  }
  if (figure.catalogue_page !== null) {
    parts.push(`page ${figure.catalogue_page}`);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * The catalogue the drawing was rendered from, e.g. "D3-X-555 Spare parts
 * catalogue 150335". Only surfaced for cross-machine drawings, where naming the
 * book is what lets a technician judge how close the variant is; the `.pdf`
 * suffix is noise in a caption.
 */
function catalogueName(figure: PartFigure): string | null {
  return presentText(figure.source_file.replace(/\.pdf$/i, ""));
}

/**
 * True when the drawing came from a pre-release ("Preliminary") catalogue draft.
 *
 * Keyed on `revision_rank`, not on the label text: rank is the field the backend
 * exposes precisely so the UI never has to parse "Preliminary Rev.1", and a
 * negative rank is authoritative even if the label is missing.
 */
function isDraftRevision(figure: PartFigure): boolean {
  return figure.revision_rank < 0;
}

/**
 * The catalogue revision, but only when naming it tells a technician something.
 *
 * `revision_rank > 0` means a maintained book (Rev.1, Rev.2) — reassuring, so it
 * is rendered plainly next to the chapter and page. Rank 0 is "base", the
 * original release, which is what all 78 machines' first catalogues are: showing
 * it on nearly every drawing would be noise that teaches the eye to skip this
 * corner of the caption, which is exactly where a Preliminary flag has to land.
 * So base renders nothing, and a negative rank is left to
 * {@link DraftRevisionNotice} rather than being stated twice.
 */
function releasedRevision(figure: PartFigure): string | null {
  if (figure.revision_rank <= 0) {
    return null;
  }
  return presentText(figure.revision_label);
}

/**
 * Where the drawing sits in which book: chapter, page and revision, whichever
 * are known. Shared by the caption and the lightbox description so the two can't
 * drift apart.
 */
function figureContext(figure: PartFigure): string | null {
  const parts = [figureSource(figure), releasedRevision(figure)].filter(
    (part): part is string => part !== null,
  );
  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * "Also appears on 11 other drawings" — a common fastener legitimately shows up
 * on dozens of assemblies, so the count stops the one drawing shown here from
 * reading as the only place the part is used.
 */
function otherOccurrences(figure: PartFigure): string | null {
  const count = figure.other_occurrence_count;
  if (count <= 0) {
    return null;
  }
  return `Also appears on ${count} other drawing${count === 1 ? "" : "s"}`;
}

/** `caption` sits above the thumbnail; `banner` spans the lightbox header. */
type NoticeVariant = "caption" | "banner";

/**
 * Two things can undermine a drawing, and they can apply at once: it may be from
 * another machine's book, or from a draft of this machine's book. Amber is
 * already the "same part, different machine" caveat, so a draft gets red — the
 * shade `suggestion-card.tsx` uses for low confidence, which is what a
 * pre-release drawing is. Two stacked notices then still read as two distinct
 * warnings rather than one repeated block.
 */
const NOTICE_TONE_CLASS = {
  crossMachine: "border-amber-200 bg-amber-50 text-amber-900",
  draft: "border-red-200 bg-red-50 text-red-900",
} as const;

interface FigureNoticeProps {
  tone: keyof typeof NOTICE_TONE_CLASS;
  icon: LucideIcon;
  variant: NoticeVariant;
  children: ReactNode;
}

/** Shared shell for the caveats shown above the thumbnail and in the lightbox. */
function FigureNotice({ tone, icon: Icon, variant, children }: FigureNoticeProps) {
  return (
    <div
      className={cn(
        "flex items-start gap-1.5 border",
        NOTICE_TONE_CLASS[tone],
        variant === "caption"
          ? "mt-1 rounded-md px-2 py-1.5 text-[11px] leading-4"
          : "border-x-0 border-t-0 px-4 py-2 text-xs leading-5",
      )}
    >
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <p className="break-words">{children}</p>
    </div>
  );
}

interface FigureCaveatProps {
  figure: PartFigure;
  variant: NoticeVariant;
}

/**
 * Flags a drawing rendered from a Preliminary catalogue — a pre-release draft,
 * which `revision_rank < 0` marks as *less* authoritative than the plain first
 * release, not more.
 *
 * The product owner chose to ingest superseded and draft catalogues because a
 * dropped part may only be documented there, so coverage wins over tidiness. The
 * cost of that call is that a technician can be shown a drawing the built machine
 * never matched, and unlike a stale revision a draft was never signed off at all.
 * That is the strongest "verify before you order" signal this component has, so
 * it is flagged rather than merely stated in the caption line, and — like the
 * cross-machine caveat — it repeats in the lightbox, which covers the caption.
 */
function DraftRevisionNotice({ figure, variant }: FigureCaveatProps) {
  if (!isDraftRevision(figure)) {
    return null;
  }
  const label = presentText(figure.revision_label);

  return (
    <FigureNotice tone="draft" icon={FileWarning} variant={variant}>
      <span className="font-semibold">Preliminary catalogue.</span> This drawing comes from a
      pre-release draft
      {label && (
        <>
          {" ("}
          <span className="font-medium">{label}</span>
          {")"}
        </>
      )}
      , not a released revision, so it may not match the machine as built.
    </FigureNotice>
  );
}

/**
 * Flags a drawing that came from a different machine's catalogue, and renders
 * nothing when the drawing is from this machine's own book.
 *
 * The figure lookup ranks same-machine drawings first but deliberately does not
 * filter on it: only about half of a catalogue's item codes appear in any given
 * machine's BOM, so for many parts the only drawing that exists is a sibling
 * variant's. That is still a useful picture — but this feature exists so a
 * technician can verify a part against the machine in front of them, and an
 * unlabelled drawing from another variant quietly undermines exactly that. So
 * the caveat appears both in the caption and in the lightbox, and names the
 * catalogue so the reader can judge how close the variant is.
 */
function CrossMachineNotice({ figure, variant }: FigureCaveatProps) {
  if (figure.same_machine) {
    return null;
  }
  const catalogue = catalogueName(figure);

  return (
    <FigureNotice tone="crossMachine" icon={TriangleAlert} variant={variant}>
      <span className="font-semibold">From another machine&rsquo;s catalogue.</span> This machine
      has no drawing for this part, so the assembly shown may differ.
      {catalogue && (
        <>
          {" Shown from "}
          <span className="font-medium">{catalogue}</span>.
        </>
      )}
    </FigureNotice>
  );
}

const ZOOM_BUTTON_CLASS =
  "border-border text-muted hover:border-primary hover:text-primary flex h-6 w-6 items-center justify-center rounded-md border transition-colors disabled:opacity-40";

interface ZoomableFigureProps {
  src: string;
  alt: string;
  /**
   * Set to the pan/zoom surface so the dialog can focus it on open. Radix
   * autofocuses the close button, which would leave the zoom and pan keys dead
   * until the user tabbed into the viewer.
   */
  viewerRef: RefObject<HTMLDivElement | null>;
}

/**
 * Pan/zoom surface for one drawing, with mouse, touch and keyboard input.
 *
 * Pan is clamped to the transformed image's own edges, measured from the img's
 * layout size (`offsetWidth`/`offsetHeight`, which the CSS transform does not
 * affect) rather than the container's — the image is `object-contain`, so at
 * scale 1 it is usually narrower than the viewer and must not move at all.
 */
function ZoomableFigure({ src, alt, viewerRef }: ZoomableFigureProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const pointers = useRef(new Map<number, Point>());
  const drag = useRef<{ start: Point; offset: Point } | null>(null);
  const pinch = useRef<{ distance: number; scale: number } | null>(null);

  // Scale and offset share one state object: every gesture derives the next
  // offset from the next scale, so keeping them apart would mean nesting one
  // setter inside the other's updater, which double-applies under StrictMode.
  const [view, setView] = useState<View>(IDENTITY_VIEW);
  const [dragging, setDragging] = useState(false);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");

  /** How far the image may be shifted on each axis at a given scale. */
  const panBounds = useCallback((scale: number): Point => {
    const container = containerRef.current;
    const image = imageRef.current;
    if (!container || !image) {
      return ORIGIN;
    }
    return {
      x: Math.max(0, (image.offsetWidth * scale - container.clientWidth) / 2),
      y: Math.max(0, (image.offsetHeight * scale - container.clientHeight) / 2),
    };
  }, []);

  /** Zoom to an absolute scale. Used by pinch, which scales from gesture start. */
  const zoomTo = useCallback(
    (scale: number, focal: Point = ORIGIN) => {
      setView((current) => zoomView(current, scale, focal, panBounds));
    },
    [panBounds],
  );

  /** Zoom by a factor relative to whatever the current scale is. */
  const zoomBy = useCallback(
    (factor: number, focal: Point = ORIGIN) => {
      setView((current) => zoomView(current, current.scale * factor, focal, panBounds));
    },
    [panBounds],
  );

  const panBy = useCallback(
    (dx: number, dy: number) => {
      setView((current) => {
        const limit = panBounds(current.scale);
        return {
          ...current,
          offset: {
            x: clamp(current.offset.x + dx, -limit.x, limit.x),
            y: clamp(current.offset.y + dy, -limit.y, limit.y),
          },
        };
      });
    },
    [panBounds],
  );

  const reset = useCallback(() => setView(IDENTITY_VIEW), []);

  /** A client point expressed relative to the viewer's centre. */
  const focalFrom = useCallback((clientX: number, clientY: number): Point => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) {
      return ORIGIN;
    }
    return { x: clientX - (rect.left + rect.width / 2), y: clientY - (rect.top + rect.height / 2) };
  }, []);

  /**
   * Ref callback that also owns the wheel listener. React registers `onWheel`
   * passively, so `preventDefault` there is a no-op and ctrl+wheel would zoom
   * the whole page instead of the drawing; a native `{ passive: false }`
   * listener is the only way to claim the gesture. The returned teardown is
   * React 19 ref-callback cleanup.
   */
  const attachContainer = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      viewerRef.current = node;
      if (!node) {
        return;
      }
      const onWheel = (event: WheelEvent) => {
        event.preventDefault();
        zoomBy(
          Math.exp(-wheelDeltaPixels(event) / WHEEL_ZOOM_DIVISOR),
          focalFrom(event.clientX, event.clientY),
        );
      };
      node.addEventListener("wheel", onWheel, { passive: false });
      return () => {
        node.removeEventListener("wheel", onWheel);
        containerRef.current = null;
        viewerRef.current = null;
      };
    },
    [focalFrom, viewerRef, zoomBy],
  );

  /**
   * Begin — or resume, after a pinch — panning from `point`.
   *
   * The baseline offset is read inside the state updater rather than from the
   * render closure: a pinch that just ended may have queued an offset this
   * render hasn't seen, and anchoring the drag to a stale offset makes the
   * drawing jump. The updater returns `current` untouched, so this only latches
   * the baseline; it doesn't change the view.
   */
  const startDrag = useCallback((point: Point) => {
    setView((current) => {
      drag.current = { start: point, offset: current.offset };
      return current;
    });
    setDragging(true);
  }, []);

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    event.currentTarget.setPointerCapture(event.pointerId);

    if (pointers.current.size === 2) {
      // Second finger down: hand the gesture over to pinch-zoom.
      const [a, b] = [...pointers.current.values()];
      if (a && b) {
        pinch.current = { distance: Math.hypot(b.x - a.x, b.y - a.y), scale: view.scale };
      }
      drag.current = null;
      setDragging(false);
      return;
    }

    startDrag({ x: event.clientX, y: event.clientY });
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(event.pointerId)) {
      return;
    }
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    const gesture = pinch.current;
    if (gesture && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      if (!a || !b || gesture.distance <= 0) {
        return;
      }
      const distance = Math.hypot(b.x - a.x, b.y - a.y);
      zoomTo(
        (gesture.scale * distance) / gesture.distance,
        focalFrom((a.x + b.x) / 2, (a.y + b.y) / 2),
      );
      return;
    }

    const active = drag.current;
    if (!active) {
      return;
    }
    const nextOffset = {
      x: active.offset.x + (event.clientX - active.start.x),
      y: active.offset.y + (event.clientY - active.start.y),
    };
    // Pan is absolute against the drag origin, not incremental, so a clamped
    // edge doesn't accumulate drift over a long drag.
    setView((current) => {
      const limit = panBounds(current.scale);
      return {
        ...current,
        offset: {
          x: clamp(nextOffset.x, -limit.x, limit.x),
          y: clamp(nextOffset.y, -limit.y, limit.y),
        },
      };
    });
  };

  const endPointer = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId);

    if (pointers.current.size === 0) {
      pinch.current = null;
      drag.current = null;
      setDragging(false);
      return;
    }

    if (pointers.current.size < 2) {
      // A finger lifted out of a pinch. Hand the gesture back to panning from
      // wherever the surviving finger is now — pinch start cleared
      // `drag.current`, so otherwise that finger is stranded and can't move the
      // drawing until it is lifted and pressed again.
      pinch.current = null;
      const [remaining] = [...pointers.current.values()];
      if (remaining) {
        startDrag({ x: remaining.x, y: remaining.y });
      }
    }
  };

  const handleDoubleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (view.scale > MIN_SCALE) {
      reset();
      return;
    }
    zoomTo(DOUBLE_CLICK_SCALE, focalFrom(event.clientX, event.clientY));
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const handled = () => event.preventDefault();

    switch (event.key) {
      case "+":
      case "=":
        handled();
        zoomBy(SCALE_STEP);
        break;
      case "-":
        handled();
        zoomBy(1 / SCALE_STEP);
        break;
      case "0":
        handled();
        reset();
        break;
      case "ArrowLeft":
        handled();
        panBy(KEY_PAN_STEP, 0);
        break;
      case "ArrowRight":
        handled();
        panBy(-KEY_PAN_STEP, 0);
        break;
      case "ArrowUp":
        handled();
        panBy(0, KEY_PAN_STEP);
        break;
      case "ArrowDown":
        handled();
        panBy(0, -KEY_PAN_STEP);
        break;
      default:
        break;
    }
  };

  const zoomed = view.scale > MIN_SCALE;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        ref={attachContainer}
        role="group"
        aria-label="Drawing viewer: drag to pan, scroll or press plus and minus to zoom"
        tabIndex={0}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onDoubleClick={handleDoubleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          "focus-visible:ring-ring relative flex h-[65vh] touch-none items-center justify-center overflow-hidden bg-white select-none focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset",
          zoomed ? (dragging ? "cursor-grabbing" : "cursor-grab") : "cursor-zoom-in",
        )}
      >
        {status === "loading" && <p className="text-muted absolute text-xs">Loading drawing…</p>}
        {status === "error" ? (
          <p className="text-muted max-w-xs px-6 text-center text-xs">
            This drawing could not be loaded. Its link may have expired — reload the conversation to
            get a fresh one.
          </p>
        ) : (
          <img
            ref={imageRef}
            src={src}
            alt={alt}
            draggable={false}
            onLoad={() => setStatus("ready")}
            onError={() => {
              logger.debug("[chat] full drawing failed to load", src);
              setStatus("error");
            }}
            // Deliberate inline `style`, not a missed Tailwind class: the
            // transform changes continuously with the gesture, and there is no
            // finite set of utility classes for it. `components/ui/progress.tsx`
            // takes the same exception for the same reason. Please leave it.
            style={{
              transform: `translate(${view.offset.x}px, ${view.offset.y}px) scale(${view.scale})`,
            }}
            className={cn(
              "max-h-full max-w-full object-contain will-change-transform",
              status === "ready" ? "opacity-100" : "opacity-0",
            )}
          />
        )}
      </div>

      <div className="border-border text-muted flex items-center justify-between gap-2 border-t px-4 py-2 text-[11px]">
        <span className="hidden sm:inline">
          Drag to pan · scroll or pinch to zoom · double-click to reset
        </span>
        <div className="flex items-center gap-1">
          <span className="w-10 text-right font-mono">{Math.round(view.scale * 100)}%</span>
          <button
            type="button"
            aria-label="Zoom out"
            disabled={view.scale <= MIN_SCALE}
            onClick={() => zoomBy(1 / SCALE_STEP)}
            className={ZOOM_BUTTON_CLASS}
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label="Zoom in"
            disabled={view.scale >= MAX_SCALE}
            onClick={() => zoomBy(SCALE_STEP)}
            className={ZOOM_BUTTON_CLASS}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label="Reset zoom"
            disabled={!zoomed && view.offset.x === 0 && view.offset.y === 0}
            onClick={reset}
            className={ZOOM_BUTTON_CLASS}
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

interface PartFigureViewProps {
  figure: PartFigure;
  /** The suggested part's item code — the label to hunt for on the drawing. */
  itemId: string;
}

/**
 * Thumbnail plus caption, with the pan/zoom lightbox behind it.
 *
 * `preview_url` and `full_url` are short-lived signed Storage URLs, so nothing
 * here caches or persists them. A preview that fails to load drops the whole
 * block rather than leaving a broken image above the confirm buttons — most
 * likely the conversation has been open past the URL's hour.
 */
export function PartFigureView({ figure, itemId }: PartFigureViewProps) {
  const [open, setOpen] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const viewerRef = useRef<HTMLDivElement | null>(null);

  if (previewFailed) {
    return null;
  }

  const title = figureTitle(figure);
  const source = figureSource(figure);
  const context = figureContext(figure);
  const occurrences = otherOccurrences(figure);
  const alt = `Assembly drawing ${figure.assembly_item_id}${source ? `, ${source}` : ""}`;

  return (
    <div className="mt-2">
      <p className="text-muted text-[11px] font-medium tracking-wide uppercase">Assembly drawing</p>

      {/* Above the thumbnail, so the caveats are read before the drawing is.
          Draft first: whether the book was ever released outranks which machine
          it was drawn for. */}
      <DraftRevisionNotice figure={figure} variant="caption" />
      <CrossMachineNotice figure={figure} variant="caption" />

      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Open the assembly drawing ${title} full size`}
        className="border-border/60 hover:border-primary focus-visible:ring-ring group relative mt-1 block w-full overflow-hidden rounded-md border bg-white transition-colors focus-visible:ring-2 focus-visible:outline-none"
      >
        <img
          src={figure.preview_url}
          alt={alt}
          loading="lazy"
          onError={() => {
            logger.debug("[chat] drawing preview failed to load", figure.occurrence_id);
            setPreviewFailed(true);
          }}
          className="h-36 w-full object-contain"
        />
        <span className="text-muted group-hover:text-primary absolute top-1 right-1 rounded bg-white/85 p-1">
          <Maximize2 className="h-3.5 w-3.5" />
        </span>
      </button>

      <p className="text-muted mt-1 text-[11px] break-words">
        <span className="text-foreground/80 font-medium">{title}</span>
        {context && <span className="ml-1">· {context}</span>}
      </p>
      <p className="text-muted text-[11px] break-words">
        Look for <span className="text-foreground/80 font-mono">{itemId}</span> on the drawing
      </p>
      {occurrences && <p className="text-muted text-[11px]">{occurrences}</p>}

      <Dialog open={open} onOpenChange={setOpen}>
        {/* `border-border` because dialog.tsx ships a bare `border`, which
            Tailwind 4 paints in `currentColor`. */}
        <DialogContent
          className="border-border flex max-h-[92vh] w-[96vw] max-w-6xl flex-col gap-0 p-0"
          onOpenAutoFocus={(event) => {
            // Radix focuses the close button on open, which leaves the viewer's
            // zoom/pan key handlers dead until the user tabs into it. Focus the
            // drawing surface instead — it is what the dialog is for. If it
            // somehow isn't mounted, fall through to Radix's default so focus
            // never escapes the dialog.
            const viewer = viewerRef.current;
            if (!viewer) {
              return;
            }
            event.preventDefault();
            viewer.focus();
          }}
        >
          <DialogHeader className="border-border border-b px-4 py-3 pr-12 text-left">
            <DialogTitle className="text-base">{title}</DialogTitle>
            <DialogDescription className="text-xs">
              Look for item code <span className="text-foreground/80 font-mono">{itemId}</span> on
              this drawing
              {context ? ` · ${context}` : ""}
            </DialogDescription>
          </DialogHeader>
          <DraftRevisionNotice figure={figure} variant="banner" />
          <CrossMachineNotice figure={figure} variant="banner" />
          {/* Mounted only while open, so the 4000px render is never fetched for
              a drawing nobody expanded. */}
          {open && <ZoomableFigure src={figure.full_url} alt={alt} viewerRef={viewerRef} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
